<template>
    <div
        class="cz2__name-field"
        :class="[
            {
                'is-kiosk': isKiosk,
                'is-virtualkeyboard': useVirtualKeyboardNameEntry,
            },
        ]"
    >
        <div class="cz2__name-field-label-wrapper">
            <label
                for="name-input"
                class="cz2__name-field-label"
            >
                {{ $t('NAME_OR_TITLE') }}
            </label>

            <base-input
                id="name-input"
                name="name-input"
                ref="nameInput"
                :input-text="name"
                :invalid="invalid"
                :error-text="errorText"
                :small-font="smallFont"
                :read-only="useVirtualKeyboardNameEntry || isKiosk"
                :outlined="isKiosk"
                :maximum-length="maxLength"
                :inputPlaceholder="$t('NAME_PLACEHOLDER')"
                labelled-by="step-header-title"
                @input="updateName"
                class="cz2__name-field-input"
            />
        </div>

        <div
            v-if="isKiosk || useVirtualKeyboardNameEntry"
            class="cz2__name-field-keyboard"
        >
            <keyboard
                :value="name"
                @input="updateNameKeyboard"
                @quotes="quotes"
                :layouts="[
                    '1234567890{DELETE:backspace}|QWERTYUIOP_|ASDFGHJKL-+|{@#Ñ:goto:1}ZXCVBNM,.@|{:space}',
                    `!@#$%^&*(){DELETE:backspace}|ÁÉÍÓÚÜÑ¡/\\_|:;||<=>[]~-+|{ABC:goto:0}{{}}'\`{″:quotes}¿?,.@|{:space}`,
                ]"
            />
        </div>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__name-field {
            width: 100%;
            pointer-events: auto;

            .cz2__name-field-label-wrapper {
                width: 100%;
            }

            .cz2__name-field-label {
                display: block;
                margin-left: 8px;
                margin-bottom: 4px;
                font-family: 'Proxima Nova';
                font-size: 12px;
                font-weight: 600;
                color: #000;
            }

            .cz2__name-field-input {
                margin-bottom: 36px;

                width: 100%;
                display: flex;
                justify-content: center;

                .cz2__input-box {
                    width: 100%;
                }

                .cz2__input-box input {
                    padding: 0 12px;
                }
            }

            .cz2__input-box__error {
                margin-left: 12px;
                color: #DB0000 !important;

                &::before {
                    margin-right: 0 !important;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__name-field {
            .cz2__name-field-input {
                margin-bottom: 20px;

                .cz2__input-box {
                    width: 600px;
                }

                // Make room for longer error message.
                .cz2__input-box__error {
                    width: 100vw;
                    left: 50%;
                    transform: translateX(-50%);
                    text-align: center;
                    font-size: 24px;
                }
            }
        }
    }
</style>

<script>
    import Keyboard from './ui/Keyboard.vue';

    import BaseInput from './ui/BaseInput.vue';

    export default {
        components: {
            Keyboard,
            BaseInput,
        },

        props: {
            validate: {
                type: Number,
                required: true,
            },
        },

        data() {
            const name = this.$store.state.customizer.boxName;

            let useName = '';

            if (name) {
                if (name.controller) {
                    useName = name.text;
                } else {
                    useName = name;
                }
            }

            return {
                invalid: null,
                errorText: '',
                name: useName,
                maxLength: 18,
            };
        },

        computed: {
            boxName() {
                return this.$store.state.customizer.boxName;
            },

            options() {
                return this.$store.state.customizer.options;
            },

            isKiosk() {
                return this.options && this.options.isKiosk;
            },

            useVirtualKeyboardNameEntry() {
                if (this.options && this.options.useVirtualKeyboardNameEntry) {
                    return true;
                }

                return false;
            },

            smallFont() {
                return (this.name || '').length > 8;
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            isMobile() {
                return this.$store.state.customizer.isMobile;
            },
        },

        watch: {
            validate() {
                this.validateName();
            },
        },

        mounted() {
            this.setFocusOnInput();
        },

        methods: {
            quotes() {
                this.updateNameKeyboard(`${this.name}"`);
            },

            updateNameKeyboard(newValue) {
                // updateName will trim the name and remove trailing spaces.
                // You can still enter names with spaces because <input> tracks the internal state.
                //
                // But we need to allow the screen keyboard to enter trailing spaces too.
                // If we use updateName, the user can never enter a space.
                //
                // So, instead, if a newly entered name only differs by trailing spaces,
                // simply update the state but skip validations.
                //
                // Only if the new name ends with a space. The screen keyboard does not have a way to move the cursor.
                if (newValue && newValue.substr(newValue.length - 1) === ' ') {
                    const newWithoutSpaces = newValue.replace(/\s+$/, '');

                    if (newWithoutSpaces === this.name) {
                        this.name = newValue;
                    } else {
                        this.updateName(newValue);
                    }
                } else {
                    this.updateName(newValue);
                }
            },

            updateName(newValue) {
                this.name = newValue.trim();

                this.$store.dispatch('updateBoxName', this.name).then((useName) => {
                    this.name = useName;

                    if (newValue.length > this.maxLength) {
                        this.$nextTick(() => {
                            this.invalid = true;
                            this.errorText = this.$t('BOX_NAME_ERROR');
                        });
                    } else {
                        this.invalid = false;
                        this.errorText = '';
                    }
                });
            },

            validateName() {
                this.viewController.reviewText(this.name).then((result) => {
                    if (result.hasProfanity) {
                        this.invalid = true;
                        this.errorText = this.$t('BOX_NAME_ERROR_PROFANITY');
                        this.$emit('done', false);

                        // Clear profanity from screen on kiosk
                        if (this.isKiosk) {
                            this.name = '';
                            this.$store.dispatch('updateBoxName', '');
                        }
                    } else {
                        this.invalid = false;
                        this.$emit('done', true);
                    }
                }).catch(() => {
                    this.invalid = true;
                    this.errorText = this.$t('BOX_NAME_ERROR_PROFANITY');
                    this.$emit('done', false);
                });
            },

            setFocusOnInput() {
                if (!this.isMobile) {
                    const inputElement = this.$refs.nameInput.$el.children[0].querySelector('input');

                    inputElement.focus();
                }
            },
        },
    };
</script>
